import { useState } from 'react';
import { useInView } from '../../../hooks/useInView';

const faqs = [
  {
    q: 'Who provides professional video production near me?',
    a: 'Immanuel Production Group offers professional corporate video production and broadcast-grade services for businesses in Central Florida. With 20+ years of experience, they handle everything from concept to final delivery — for brands, organizations, and campaigns that demand cinematic excellence and proven results.',
  },
  {
    q: 'How much does video production cost?',
    a: 'Corporate video production costs vary by project scope, length, and complexity. Productions range from focused single-location shoots to full broadcast campaigns with scripting, multiple locations, professional crew, and advanced post-production. Contact us for a custom quote tailored to your specific goals.',
  },
  {
    q: 'How long does video production take?',
    a: 'Timelines depend on the project type. Most corporate video productions take anywhere from a few days to several weeks, covering planning, filming, and post-production. Rush delivery is available for time-sensitive campaigns — we work around your deadlines and deliver on schedule every time.',
  },
  {
    q: 'Do you offer live streaming services for events?',
    a: 'Yes — we provide multi-camera, broadcast-grade live streaming services for events, church services, conferences, and brand activations in Central Florida and beyond. Our team manages full technical production from setup through global distribution, ensuring professional-quality output every time.',
  },
  {
    q: 'Are you available outside Central Florida for video production?',
    a: 'Absolutely. While headquartered in Oviedo, Central Florida, our team is available for travel productions nationally and internationally. We regularly serve clients across Florida and have produced broadcast-quality content in multiple states and countries for national brands.',
  },
  {
    q: 'Do you offer bilingual video production services?',
    a: 'Yes — our bilingual production team delivers broadcast-level content in both English and Spanish. This makes us the ideal corporate video production partner for brands targeting diverse audiences across Florida, the U.S., and Latin American markets.',
  },
];

export default function FAQ() {
  const { ref, inView } = useInView();
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section ref={ref as React.RefObject<HTMLDivElement>} className="py-36 bg-[#0c0c0c] relative" id="faq">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/12 to-transparent" />

      <div className="max-w-3xl mx-auto px-8 lg:px-14">
        {/* Header */}
        <div className={`text-center mb-20 transition-all duration-800 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="font-sans text-[0.58rem] tracking-[0.45em] uppercase text-gold mb-6 font-medium">Voice Search Ready</p>
          <h2 className="font-serif font-bold text-white leading-tight" style={{ fontSize: 'clamp(2.5rem, 4.5vw, 4.5rem)', letterSpacing: '-0.02em' }}>
            Frequently Asked<br />
            <em className="not-italic text-gold">Questions</em>
          </h2>
          <p className="font-sans text-sm text-white/30 mt-5 max-w-xs mx-auto leading-relaxed">Optimized for Google, Siri, Alexa, and AI-powered search engines.</p>
        </div>

        {/* FAQ list */}
        <div
          className={`space-y-2.5 transition-all duration-800 delay-150 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`border-l-2 transition-all duration-300 overflow-hidden ${
                open === i ? 'border-gold bg-white/[0.025]' : 'border-white/8 hover:border-white/20 bg-white/[0.01]'
              }`}
            >
              <button
                className="w-full flex items-center justify-between gap-6 px-8 py-6 text-left cursor-pointer group"
                onClick={() => setOpen(open === i ? null : i)}
              >
                <span className={`font-sans text-sm font-medium leading-snug transition-colors duration-300 ${open === i ? 'text-white' : 'text-white/55 group-hover:text-white/80'}`}>
                  {faq.q}
                </span>
                <div className={`w-8 h-8 flex items-center justify-center border flex-shrink-0 transition-all duration-300 ${open === i ? 'border-gold bg-gold text-[#020202]' : 'border-white/12 text-white/35 group-hover:border-white/30'}`}>
                  <i className={`${open === i ? 'ri-subtract-line' : 'ri-add-line'} text-sm`} />
                </div>
              </button>
              {open === i && (
                <div className="px-8 pb-8">
                  <div className="w-8 h-px bg-gold/40 mb-4" />
                  <p className="font-sans text-sm text-white/48 leading-[1.95]">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
